import React from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '../shared/Button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getPlanLabel } from '../../utils/planLabels';
import { formatCurrency } from '../../utils/currency';
import {
  getSubscriptionPlan,
  isPassesModel,
  PASS_DURATION_LABEL_KEYS,
  PASS_TIERS,
} from '../../config/pricing';

interface PlanComparisonProps {
  currentPlan: string;
  onChangePlan: (plan: string) => void;
}

interface PlanFeatures {
  [key: string]: boolean | string;
}

interface Plan {
  name: string;
  price: number;
  features: PlanFeatures;
  featureKeys: string[];
}

const plans: Plan[] = [
  {
    name: 'level_bundle',
    price: getSubscriptionPlan('level_bundle')?.price ?? 0,
    features: {
      'practiceExams': 'unlimited',
      'allSections': true,
      'a2Level': true,
      'b1Level': false,
      'progressTracking': true,
      'prioritySupport': false,
    },
    featureKeys: ['practiceExams', 'allSections', 'a2Level', 'b1Level', 'progressTracking', 'prioritySupport'],
  },
  {
    name: 'full_access',
    price: getSubscriptionPlan('full_access')?.price ?? 0,
    features: {
      'practiceExams': 'unlimited',
      'allSections': true,
      'a2Level': true,
      'b1Level': true,
      'progressTracking': true,
      'prioritySupport': true,
    },
    featureKeys: ['practiceExams', 'allSections', 'a2Level', 'b1Level', 'progressTracking', 'prioritySupport'],
  },
];

const featureKeys = ['practiceExams', 'allSections', 'a2Level', 'b1Level', 'progressTracking', 'prioritySupport'];

export const PlanComparison: React.FC<PlanComparisonProps> = ({
  currentPlan,
  onChangePlan,
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const currentIndex = plans.findIndex(p => p.name === currentPlan);
    const isFreeUser = currentIndex === -1;

    const handleCta = (plan: Plan) => {
      if (isFreeUser) {
        navigate(`/checkout?plan=${plan.name}`);
      } else {
        onChangePlan(plan.name);
      }
    };

  if (isPassesModel) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {PASS_TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`relative flex flex-col rounded-2xl border-2 p-6 ${
                tier.recommended ? 'border-secondary bg-secondary/5' : 'border-gray-200'
              }`}
            >
              {tier.recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-white">
                  {t('landing.pricing.passes.recommended')}
                </span>
              )}
              <h3 className="mb-1 text-xl font-bold">{t(`${tier.i18nKey}.name`)}</h3>
              <p className="mb-3 text-sm font-medium text-secondary">
                {t(PASS_DURATION_LABEL_KEYS[tier.duration])}
              </p>
              <div className="mb-1 text-3xl font-bold text-primary">
                {formatCurrency(tier.price, i18n.language)}
              </div>
              <div className="mb-4 text-xs text-gray-500">
                {t('landing.pricing.passes.oneTime')} · {t('landing.pricing.passes.vatIncluded')}
              </div>
              <ul className="mb-6 space-y-2">
                {(t(`${tier.i18nKey}.features`, { returnObjects: true }) as string[]).map(
                  (feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 w-5 h-5 flex-shrink-0 text-green-500" />
                      <span>{feature}</span>
                    </li>
                  )
                )}
              </ul>
              <Button
                variant={tier.recommended ? 'primary' : 'secondary'}
                className="mt-auto w-full"
                // Pass checkout does not exist yet: Stripe is still on the
                // subscription model, so this links to pricing, not a fake purchase.
                onClick={() => navigate('/pricing')}
              >
                {t(`${tier.i18nKey}.cta`)}
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Mobile: Cards */}
      <div className="md:hidden space-y-6 p-6">
        {plans.map((plan) => {
          const isCurrent = currentPlan === plan.name;
          return (
            <div 
              key={plan.name} 
              className={`border-2 rounded-xl p-6 ${
                isCurrent ? 'border-primary bg-primary/5' : 'border-gray-200'
              }`}
            >
              {isCurrent && (
                <span className="inline-block bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  {t('payments.planComparison.currentPlan')}
                </span>
              )}
              <h3 className="text-xl font-bold mb-2">{getPlanLabel(plan.name, t)}</h3>
              <div className="text-3xl font-bold text-primary mb-4">
                €{plan.price.toFixed(2)}
                <span className="text-sm text-gray-600 font-normal">{t('payments.planComparison.perMonth')}</span>
              </div>

              <ul className="space-y-2 mb-6">
                {plan.featureKeys.map((key) => {
                  const value = plan.features[key];
                  const hasFeature = value === true || typeof value === 'string';
                  return (
                    <li key={key} className="flex items-start gap-2 text-sm">
                      {hasFeature ? (
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={hasFeature ? '' : 'text-gray-400'}>
                        {t(`payments.planComparison.features.${key}`)}
                        {value === 'unlimited' && `: ${t('payments.planComparison.features.unlimited')}`}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {isCurrent ? (
                <Button variant="tertiary" className="w-full" disabled>
                  {t('payments.planComparison.currentPlan')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => handleCta(plan)}
                >
                  {isFreeUser || plans.indexOf(plan) > currentIndex
                    ? t('payments.planComparison.upgradeTo')
                    : t('payments.planComparison.switchTo')} {getPlanLabel(plan.name, t)}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-6 py-4 text-left font-semibold text-gray-700">{t('payments.planComparison.feature')}</th>
              {plans.map((plan) => {
                const isCurrent = currentPlan === plan.name;
                return (
                  <th key={plan.name} className="px-6 py-4 text-center">
                    {isCurrent && (
                      <span className="inline-block bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">
                        {t('payments.planComparison.currentPlan')}
                      </span>
                    )}
                    <div className="font-bold text-lg mb-1">{getPlanLabel(plan.name, t)}</div>
                    <div className="text-2xl font-bold text-primary">
                      €{plan.price.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-600">{t('payments.planComparison.perMonth')}</div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {featureKeys.map((key, index) => (
              <tr
                key={key}
                className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
              >
                <td className="px-6 py-4 text-sm font-medium text-gray-700">{t(`payments.planComparison.features.${key}`)}</td>
                {plans.map((plan) => {
                  const value = plan.features[key];
                  return (
                    <td key={plan.name} className="px-6 py-4 text-center">
                      {typeof value === 'boolean' ? (
                        value ? (
                          <Check className="w-6 h-6 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-6 h-6 text-gray-300 mx-auto" />
                        )
                      ) : (
                        <span className="font-semibold text-primary">{t('payments.planComparison.features.unlimited')}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}

            <tr className="border-t-2 border-gray-200 bg-gray-50">
              <td className="px-6 py-6"></td>
              {plans.map((plan) => {
                const isCurrent = currentPlan === plan.name;
                const planIndex = plans.findIndex(p => p.name === plan.name);
                
                return (
                  <td key={plan.name} className="px-6 py-6 text-center">
                    {isCurrent ? (
                      <Button variant="tertiary" size="small" disabled>
                        {t('payments.planComparison.currentPlan')}
                      </Button>
                    ) : (
                      <Button
                        variant={planIndex > currentIndex ? 'primary' : 'secondary'}
                        size="small"
                        onClick={() => handleCta(plan)}
                      >
                        {isFreeUser || planIndex > currentIndex
                          ? t('payments.planComparison.upgradeTo')
                          : t('payments.planComparison.switchTo')} {getPlanLabel(plan.name, t)}
                      </Button>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlanComparison;
